/*
 * status-category-List.tsx
 * This status category list Component is used for representing all the status category in list Form.
 */
import useFetch from "../../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../../components/page-header/page-header";
import useToast from "../../../../hooks/useToast";
import Utils from "../../../../utils";
import { TConfigStatus, TFilterModel } from "../../../../types/config";
import { ePrivileges, eResultCode } from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  debounceTime,
  defaultFilterParams,
  PageSize,
} from "../../../../utils/constants";
import {
  Button,
  Drawer,
  Dropdown,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { TProjectCategory } from "../../../../types/config";
import CustomDrawer from "../../../../components/drawer";
import {
  GetConfigGroups,
  GetProjectCategoryList,
  GetStateList,
} from "../../../../utils/api.constant";
import { useNavigate } from "react-router-dom";
import utils from "../../../../utils";
import ActionCard from "../../../../components/action-card/action-card";
import { UserContext } from "../../../../context";
import {
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../../components/configurable-columns";
import StatusCategoryForm from "./form";
import ProjectCategoryAndStatusForm from "./form";

export default function ProjectCategoryAndStatusMapping() {
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TConfigStatus[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();

  const initialColumns: any[] = [
    {
      title: "Sr. No.",
      key: "serialNumber",
      width: "8%",
      render: (_: any, __: TConfigStatus, index: number) => index + 1,
    },
    {
      title: "Project Category",
      dataIndex: "name",
      key: "name",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "name"),
      sorter: (a: TConfigStatus, b: TConfigStatus) =>
        a.name.localeCompare(b.name),
      onFilter: (value: string | number | boolean, record: TConfigStatus) =>
        record.name?.includes(value as string),
    },
    {
      title: "Status",
      dataIndex: "categoryStatus",
      key: "categoryStatus",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "categoryStatus"),
      sorter: (a: TConfigStatus, b: TConfigStatus) =>
        a.categoryStatus.toString().localeCompare(b.categoryStatus.toString()),
      onFilter: (value: string | number | boolean, record: TConfigStatus) =>
        record.categoryStatus?.toString().includes(value as string),
    },
    {
      title: "Action",
      key: "actions",
      align: "left",
      width: "4%",
      render: (item: TConfigStatus) => (
        <Dropdown
          trigger={["click"]}
          getPopupContainer={() => document.body}
          menu={{
            items: [
              Utils.isUserHasAccess(
                context.user?.privilegeList,
                ePrivileges.EDITSTATUSCONFIGURATION,
              ) && {
                key: "edit",
                label: (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <EditOutlined style={{ fontSize: 12 }} />
                        Edit
                      </div>
                    }
                    component={ProjectCategoryAndStatusForm}
                    componentProps={{
                      id: item.id,
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Edit Status Configuration"
                  />
                ),
              },
            ].filter(Boolean) as any,
          }}
        >
          <MoreOutlined style={{ fontSize: 20, cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState(
    defaultFilterParams.searchText,
  );

  const context: any = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, [debouncedSearchText]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          ...filterParams,
          searchText: filterParams.searchText,
          pageSize: PageSize,
        },
      };

      const response = await post(GetProjectCategoryList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map(
          (item: TConfigStatus, index: number) => ({
            key: (item.id ?? "").toString(),
            name: item.name,
            categoryStatus: item.categoryStatus
              .map((status) => status.name)
              .join(", "),
            id: item.id,
          }),
        );
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);

        const nameFilters = Array.from(
          new Set(formattedData.map((item: TConfigStatus) => item.name)),
        )
          .filter((name) => name)
          .map((name) => ({ text: name, value: name }));

        const categoryStatusFilters = Array.from(
          new Set(
            formattedData
              .map((item: TConfigStatus) => item.categoryStatus)
              .flatMap((status: string) => status.split(", ")), // Split comma-separated values
          ),
        )
          .filter((status) => status)
          .map((categoryStatus) => ({
            text: categoryStatus,
            value: categoryStatus,
          }));

        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.key === "name") {
              return { ...col, filters: nameFilters };
            }
            if (col.key === "categoryStatus") {
              return { ...col, filters: categoryStatusFilters };
            }
            return col;
          }),
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  // Update search text state
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(filterParams.searchText);
    }, debounceTime); // Set debounce delay to 300 milliseconds

    return () => {
      clearTimeout(timer); // Clear the timer on each update
    };
  }, [filterParams.searchText]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action",
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };
  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Status Configuration`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDSTATUSCONFIGURATION,
          ) ? (
            <CustomDrawer
              size="default"
              trigger={
                <Button disabled={isLoading} type="primary">
                  Configuration
                </Button>
              }
              component={ProjectCategoryAndStatusForm}
              componentProps={{
                id: 0, // Pass necessary props for ServicePartnerForm
                onRefreshList: () => {
                  navigate("/dashboard/config-project");
                  !Utils.hasDifference(defaultFilterParams, filterParams) &&
                    fetchData();
                  setFilterParams(defaultFilterParams);
                },
              }}
              title="Add New Status Configuration"
            />
          ) : null}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            prefix={
              <SearchOutlined style={{ color: "var( --bs-search-style)" }} />
            }
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
          {/* <FontAwesomeIcon
           icon={faFilter}
           className="listFilterIcons"
         ></FontAwesomeIcon> */}
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
